import React, { useMemo, useRef } from 'react';
import { BackgroundThemeProps } from '../backgroundThemes';
import { useCanvasLoop } from '../useCanvasLoop';

const CHARACTERS =
  'アイウエオカキクケコサシスセソタチツテトナニヌネノ0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const FONT_SIZE = 16;

export const MatrixRainBackground: React.FC<BackgroundThemeProps> = ({ tone, activity }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null!);

  const renderer = useMemo(() => {
    let columns = 0;
    let drops: number[] = [];
    const headColor = tone === 'dark' ? 'rgba(190, 255, 219, 0.88)' : 'rgba(13, 148, 136, 0.75)';
    const trailColor = tone === 'dark' ? 'rgba(20, 184, 166, 0.45)' : 'rgba(15, 118, 110, 0.35)';
    const staticColor = tone === 'dark' ? '45, 212, 191' : '15, 118, 110';

    const randomCharacter = () => CHARACTERS[Math.floor(Math.random() * CHARACTERS.length)];

    return {
      setup(ctx: CanvasRenderingContext2D, size: { width: number; height: number }) {
        columns = Math.ceil(size.width / FONT_SIZE);
        drops = Array.from({ length: columns }, () =>
          Math.floor(Math.random() * (size.height / FONT_SIZE))
        );
        ctx.clearRect(0, 0, size.width, size.height);
      },
      draw(ctx: CanvasRenderingContext2D, size: { width: number; height: number }) {
        ctx.globalCompositeOperation = 'destination-out';
        ctx.fillStyle = 'rgba(0, 0, 0, 0.14)';
        ctx.fillRect(0, 0, size.width, size.height);
        ctx.globalCompositeOperation = 'source-over';
        ctx.font = `${FONT_SIZE}px monospace`;

        for (let columnIndex = 0; columnIndex < drops.length; columnIndex += 1) {
          const character = randomCharacter();
          const x = columnIndex * FONT_SIZE;
          const y = drops[columnIndex] * FONT_SIZE;

          ctx.fillStyle = headColor;
          ctx.fillText(character, x, y);
          ctx.fillStyle = trailColor;
          ctx.fillText(character, x, y - FONT_SIZE);

          if (y > size.height && Math.random() > 0.975) {
            drops[columnIndex] = 0;
          } else {
            drops[columnIndex] += 1;
          }
        }
      },
      drawStatic(ctx: CanvasRenderingContext2D, size: { width: number; height: number }) {
        ctx.clearRect(0, 0, size.width, size.height);
        ctx.font = `${FONT_SIZE}px monospace`;

        for (let columnIndex = 0; columnIndex < drops.length; columnIndex += 1) {
          const x = columnIndex * FONT_SIZE;
          const rows = Math.ceil(size.height / FONT_SIZE);

          for (let rowIndex = 0; rowIndex < rows; rowIndex += 4) {
            const opacity = 0.12 + Math.random() * 0.28;
            ctx.fillStyle = `rgba(${staticColor}, ${opacity})`;
            ctx.fillText(randomCharacter(), x, rowIndex * FONT_SIZE);
          }
        }
      },
    };
  }, [tone]);

  useCanvasLoop(canvasRef, renderer, activity);

  return <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />;
};
